import Core            from "@/core/Core";
import PartnersDAO     from "@/domains/Partners/PartnersDAO";
import Partner         from "@/domains/Partners/Partner";

import type RpcDataPagination from "@/core/services/Rpc/RpcDataPagination";
import type RpcDataSort       from "@/core/services/Rpc/RpcDataSort";
import ContextMenuItem from "@/lib/components/VContextMenu/ContextMenuItem";

class PartnersService {

  static isOperationsAllowedList = [
    { id: true,  name: 'Полный'   },
    { id: false, name: 'Тестовый' },
  ];

  static partnerTypeList = [
    { id: 'seller',    name: 'Поставщик'             },
    { id: 'buyer',     name: 'Покупатель'            },
    { id: 'deliverer', name: 'Транспортная компания' },
  ];

  static getEditSectionTabs(isBuyer: boolean) {
    if (isBuyer) {
      return [
        { id: 'persons',           name: 'Контакты'              },
        { id: 'main',              name: 'Основное'              },
        { id: 'supplyOffers',      name: 'Разрешения на поставку' },
        { id: 'files',             name: 'Файлы'                 },
        { id: 'orders',            name: 'История заказов'       },
      ];
    } else {
      return [
        { id: 'contacts', name: 'Контакты' },
        { id: 'main',     name: 'Основное' },
      ];
    }
  }

  /** ------ Чтение ------ */

  /**
   * Загрузить одного партнёра по ID
   */
  static async get(id: number): Promise<Partner | null> {
    const response = await PartnersDAO.get(id);

    return Core.utils.hydrate(response.data, Partner.fromServer);
  }

  /**
   * Пагинированный список — формат под DataGridLoader.
   */
  static async getListPaginated(
    params?: Parameters<typeof PartnersDAO.getList>[0] & {
      pagination?: RpcDataPagination;
      sort?: RpcDataSort;
    }
  ): Promise<{ data: Partner[]; pagination: RpcDataPagination }> {
    const { pagination, sort, ...filters } = params ?? {};
    const response = await PartnersDAO.getList(filters, pagination, sort);

    return {
      data:       Core.utils.hydrateArray(response.data, Partner.fromServer),
      pagination: response.meta.pagination,
    };
  }

  /**
   * Плоский список партнёров. Для не-гридовых сценариев (диалоги, автокомплиты).
   */
  static async getList(
    params?: Parameters<typeof PartnersDAO.getList>[0]
  ): Promise<Partner[]> {
    return (await this.getListPaginated(params)).data;
  }

  /**
   * Поиск продавцов по строке. Используется как searchFunction
   * в фильтрах реестров и автокомплитах.
   */
  static searchSellers(pattern: string): Promise<Partner[]> {
    return PartnersService.getList({ pattern, types: ['seller'] });
  }

  /**
   * Поиск покупателей по строке.
   */
  static searchBuyers(pattern: string): Promise<Partner[]> {
    return PartnersService.getList({ pattern, types: ['buyer'] });
  }

  /** ------ CRUD ------ */

  static async add(partner: Partner): Promise<boolean> {
    const response = await PartnersDAO.add(partner);

    if (response.ok) {
      partner.id = response.data.id;
      Core.showNotification.success('Партнёр успешно добавлен');
    }

    return response.ok;
  }

  static async update(partner: Partner): Promise<boolean> {
    const response = await PartnersDAO.update(partner);

    if (response.ok) {
      Core.showNotification.success('Партнёр успешно обновлён');
    }

    return response.ok;
  }

  static async delete(id: number): Promise<boolean> {
    const response = await PartnersDAO.delete(id);

    if (response.ok) {
      Core.showNotification.success('Партнёр успешно удалён');
    }

    return response.ok;
  }

  static async deleteWithConfirm(partner: Partner): Promise<boolean> {
    const confirmed = await Core.confirm('Подтверждаете удаление партнёра?');

    if (confirmed) {
      return await this.delete(partner.id);
    } else {
      return false;
    }
  }

  /**
   * Показывает карточку партнёра. Создание и редактирование — один вход:
   * partnerOrId — существующий партнёр/id, null — создание нового.
   * Промис резолвится по закрытию карточки — вызывающий сам решает, рефрешить ли
   * список.
   */
  static showPartnerCard(partnerOrId: Partner | number | null): Promise<boolean> {
    return Core.dialogs.PartnerCard.showAsPromise({ partnerOrId });
  }

  /**
   * Открывает карточку партнёра в режиме создания.
   * Возвращает созданного партнёра либо null, если оператор закрыл карточку.
   *
   * Карточка резолвится булевым и созданную сущность через промис не отдаёт,
   * поэтому забираем её событием partner:created — промис собирается здесь.
   */
  static createPartner(): Promise<Partner | null> {
    return new Promise((resolve) => {
      Core.dialogs.PartnerCard.show(
        { partnerOrId: null },
        {
          'partner:created': (partner: Partner) => { resolve(partner); },
          close:             () => { resolve(null); },
        },
      );
    });
  }

  static get contextMenu(): ContextMenuItem[] {
    return [
      new ContextMenuItem(
        'Открыть',
        'open_in_new',
        (partner: Partner) => { return this.showPartnerCard(partner); },
        () => null,
      ),
      new ContextMenuItem(
        'Удалить',
        'delete',
        (partner: Partner) => { return this.deleteWithConfirm(partner); },
        () => null,
      ),
    ];
  }
}

export default PartnersService;
